import { OrderLineTypes } from "@repo/database";
import { generateRandomString } from "@repo/utils";
import { MiscProductSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import { useCourse } from "~/hooks/use-course";
import { useGeneral } from "~/hooks/use-general";
import { usePrinter } from "~/hooks/use-printer";
import { useProduct } from "~/hooks/use-product";
import { useCartManager } from "~/managers/use-cart-manager";
import { usePermissionStore } from "~/stores/use-permission-store";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";

export default function MiscProductForm({ onClose }: { onClose: () => void }) {
  const { vatRates } = useGeneral();
  const { productCategories } = useProduct();
  const { currentEmployee } = usePermissionStore();
  const { printRoutes } = usePrinter();
  const { courses } = useCourse();

  const { t, i18n } = useTranslation("common");

  const { addToCart } = useCartManager();

  const form = useForm({
    reValidateMode: "onChange",
    schema: MiscProductSchema(i18n),
    defaultValues: {
      name: "",
      price: 0,
      vatRate: vatRates[0]?.id ?? "",
      printRouteId: "",
      categoryId: "",
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    const category = productCategories.find(
      (category) => category.id === data.categoryId,
    );
    const vatRate = vatRates.find((vatRate) => vatRate.id === data.vatRate);
    if (!vatRate) {
      return;
    }
    void addToCart({
      ordered: false,
      printed: false,
      reference: generateRandomString(8),
      name: data.name,
      preparationName: data.name,
      quantity: 1,
      originalQuantity: 1,
      value: data.price,
      vatRate: vatRate.value,
      vatRateName: vatRate.name,
      createdAt: new Date(),
      courseId: data.courseId,
      employeeId: currentEmployee?.id,
      type: OrderLineTypes.product,
      printRouteId: data.printRouteId,
      categoryId: data.categoryId,
      notifyPrintRouteId: category?.notifyPrintRouteId,
      categoryName: category?.name,
      turnoverGroupName: category?.turnoverGroupName,
      turnoverGroupLedgerNumber: category?.turnoverGroupLedgerNumber,
    });
    onClose();
  });
  return (
    <Form {...form}>
      <form
        id="misc-product-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("MiscProduct.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("MiscProduct.name")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("MiscProduct.price")}</FormLabel>
              <FormControl>
                <ModalInput type="decimal" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="categoryId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("MiscProduct.category")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder="Select category..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    const selectedCategory = productCategories.find(
                      (category) => category.id === value[0],
                    );
                    if (selectedCategory) {
                      form.setValue(
                        "courseId",
                        selectedCategory.courseId ?? undefined,
                      );
                      form.setValue(
                        "vatRate",
                        selectedCategory.vatRateId ?? "",
                      );
                      form.setValue(
                        "printRouteId",
                        selectedCategory.printRouteId ?? "",
                      );
                    }
                  }}
                  options={productCategories.map((category) => ({
                    label: category.name,
                    value: category.id,
                  }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="courseId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("MiscProduct.course")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder="Select order chart..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={courses.map((course) => ({
                    label: course.name,
                    value: course.id,
                  }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="vatRate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("MiscProduct.vat_rate")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder="Select VAT rate..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={vatRates
                    .sort((a, b) => a.value - b.value)
                    .map((vatRate) => ({
                      label: vatRate.name,
                      value: vatRate.id,
                    }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="printRouteId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("MiscProduct.print_route")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder="Select print route..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={printRoutes.map((printRoute) => ({
                    label: printRoute.name,
                    value: printRoute.id,
                  }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
